/**
 * How many tokens a model actually spends on a project, relative to the corpus it was
 * estimated from.
 *
 * Every project bundle in this engine is estimated from a corpus that is 100 % Anthropic,
 * then priced on each model as if that model would spend the same volume. METHOD.md stated the
 * assumption and predicted its error: other models would be "lower bounds, needing more turns".
 *
 * The benchmarks did not bear that out, and did not simply reverse it either. Against the clean
 * opus-5 run — same fresh folder, same minimal instruction and same max effort as every other
 * model's run — the non-Anthropic models we measured spent 0.25x (grok-4.6) to 1.17x
 * (deepseek-v4.1-flash) of its volume: three below, one above. A Claude-shaped volume would
 * misprice them by up to four times, in either direction.
 *
 * So the bundle is scaled per model before it is priced. Three statuses, and the interface
 * shows which one applies — a factor is never silently assumed:
 *
 *   'measured'  a full nine-task run of ours, all in, against opus-5 on the same statements
 *   'family'    an Anthropic model we did not run: the corpus IS this family, so 1.0 is the
 *               natural reading, not a guess
 *   'assumed'   a non-Anthropic model we did not run: kept at 1.0 for want of a measurement.
 *               The interface names a likely direction only when every measured model points
 *               the same way (see `unmeasuredBiasFr`); today they do not.
 */

export type ConsumptionBasis = 'measured' | 'family' | 'assumed'

export interface Consumption {
  /** Tokens this model spends per token of the corpus's Claude-shaped estimate. */
  factor: number
  basis: ConsumptionBasis
  note_fr: string
}

/** The run everything is measured against. opus-5 is the one Anthropic model we benched. */
export const CONSUMPTION_REFERENCE = 'anthropic/claude-opus-5'

/**
 * Measured on the same nine statements, all in — tasks, orchestration, and attempts abandoned
 * on the way, because they were billed.
 *
 * The reference is opus-5's SECOND run (data/bench-opus5-run2.json, totaux.all_in.total): the
 * only one made under the same conditions as the other models' runs, and matched to the token
 * by the run's own report. It includes 2,692,013 tokens (2.5 %) of Sonnet Explore agents that
 * one task spawned: all in means all in. The first run (11–12/09, 224,548,498 tokens) is no
 * longer the reference: protocol v1 without dependency installs, deliverables inside this repo,
 * an orchestration window flagged as an upper bound (26.0M, 11.6 %), effort not recorded.
 *
 * READ WITH ITS UNCERTAINTY. One run per model; running the same model on the same task a second
 * time moved its volume by 1.67x to 1.74x, and the reference itself moved by 0.48x overall
 * between its two runs (0.13x to 2.65x per task, partly the protocol change). The harnesses also
 * differ (Claude Code, ZCode, grok-build, DeepSeek Harness). Only grok-4.6, and probably glm-5.3,
 * sit clearly below the Claude volume; glm-5.3-flash and deepseek-v4.1-flash cannot be told
 * apart from it on one run.
 */
export const MEASURED: Record<string, { tokens: number; run_fr: string }> = {
  'anthropic/claude-opus-5': {
    tokens: 108_020_050, run_fr: 'Claude Code, effort max, 15–16/09/2026',
  },
  'z-ai/glm-5.3-flash': { tokens: 90_095_509, run_fr: 'ZCode, 12/09/2026' },
  'z-ai/glm-5.3': { tokens: 64_088_479, run_fr: 'ZCode, 13/09/2026' },
  'x-ai/grok-4.6': { tokens: 27_205_161, run_fr: 'grok-build, effort xhigh, 14/09/2026' },
  // The clean afternoon run, all subagents counted. NOT the morning attempt, which looped on a
  // keychain dialog, was resumed, and burned 256M tokens for less work.
  'deepseek/deepseek-v4.1-flash': {
    tokens: 126_280_856, run_fr: 'DeepSeek Harness, effort max, 15/09/2026',
  },
}

const ANTHROPIC = 'anthropic/'

/** The span of measured factors outside the reference family, for prose that quotes it. */
export function measuredRange(): { low: number; high: number } | null {
  const factors = Object.keys(MEASURED)
    .filter((id) => !id.startsWith(ANTHROPIC))
    .map((id) => consumptionOf(id).factor)
  return factors.length === 0 ? null
    : { low: Math.min(...factors), high: Math.max(...factors) }
}

/** « de 0,25× à 1,17× », or null when nothing outside the reference family was measured. */
export function measuredSpanFr(): string | null {
  const range = measuredRange()
  return range ? `de ${formatFactor(range.low)} à ${formatFactor(range.high)}` : null
}

/**
 * Which way the Claude volume is probably wrong for a non-Anthropic model we never ran, as a
 * clause that follows « chiffré au volume de Claude, ». A direction is named only when every
 * measured model points the same way: one model on each side of 1 and the reader is told so.
 */
export function unmeasuredBiasFr(): string {
  const range = measuredRange()
  if (range && range.high < 1) return 'ce qui le surestime probablement'
  if (range && range.low > 1) return 'ce qui le sous-estime probablement'
  return 'sans qu’on sache s’il est surestimé ou sous-estimé'
}

/** The shape of a Claude project, at the volume this model actually spends. */
export function scaleBundle<T extends { in: number; out: number; cw: number; cr: number;
  cw_5m: number; cw_1h: number }>(bundle: T, factor: number): T {
  return {
    ...bundle,
    in: bundle.in * factor, out: bundle.out * factor,
    cw: bundle.cw * factor, cr: bundle.cr * factor,
    cw_5m: bundle.cw_5m * factor, cw_1h: bundle.cw_1h * factor,
  }
}

export function consumptionOf(modelId: string): Consumption {
  const reference = MEASURED[CONSUMPTION_REFERENCE]
  const run = MEASURED[modelId]
  if (run && reference) {
    const factor = run.tokens / reference.tokens
    return {
      factor,
      basis: 'measured',
      note_fr: modelId === CONSUMPTION_REFERENCE
        ? 'Modèle de référence : sa consommation est celle que nous avons mesurée.'
        : `Consommation mesurée sur nos tâches de test : ${formatFactor(factor)} du volume de `
          + 'notre modèle de référence sur les mêmes énoncés. Ordre de grandeur, pas valeur '
          + 'exacte : d’un essai à l’autre, le même travail varie.',
    }
  }
  if (modelId.startsWith(ANTHROPIC)) {
    return {
      factor: 1,
      basis: 'family',
      note_fr: 'Même famille que le corpus d’estimation : le volume de référence s’applique tel '
             + 'quel.',
    }
  }
  const span = measuredSpanFr()
  return {
    factor: 1,
    basis: 'assumed',
    note_fr: 'Consommation non mesurée : ce modèle est chiffré au volume d’un projet Claude, '
           + `${unmeasuredBiasFr()}.`
           + (span ? ` Les modèles non-Claude que nous avons mesurés ont consommé ${span} ce `
             + 'volume.' : ''),
  }
}

function formatFactor(factor: number): string {
  return `${factor.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}×`
}
